import { useMemo } from 'react';
import { DataTable, Column } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { useBreadcrumbNavigation } from '@/lib/breadcrumbs/useBreadcrumbNavigation';
import { hasConfiguredMfaMethod } from '@/lib/auth/mfaStatus';
import { EmailsCell } from './EmailsCell';
import { OrganizationBubble } from './OrganizationBubble';
import { RoleBadge } from './RoleBadge';
import {
    type UserProfileDetailedPublicResponse,
    UserProfileDetailedResponse,
} from '@anchorbase/pillar-sdk';

interface UsersTableProps {
    users: ReadonlyArray<UserProfileDetailedResponse | UserProfileDetailedPublicResponse>;
}

type SupportedUser = UserProfileDetailedResponse | UserProfileDetailedPublicResponse;
type SupportedRoleAssignment = NonNullable<SupportedUser['default_role_assignment']>;

function getUserUuid(user: SupportedUser): string | null {
    if (!('user_uuid' in user)) {
        return null;
    }
    return user.user_uuid ?? null;
}

function getDisplayRoleAssignments(
    user: SupportedUser
): ReadonlyArray<SupportedRoleAssignment & { isDefault: boolean }> {
    const assignments: Array<SupportedRoleAssignment & { isDefault: boolean }> = [];
    if (user.default_role_assignment !== null) {
        assignments.push({
            ...user.default_role_assignment,
            isDefault: true,
        });
    }
    for (const roleAssignment of user.other_role_assignments) {
        assignments.push({
            ...roleAssignment,
            isDefault: false,
        });
    }
    return assignments;
}

function getUserKey(user: SupportedUser): string {
    const userUuid = getUserUuid(user);
    if (userUuid !== null) {
        return userUuid;
    }
    if ('user_id' in user) {
        return user.user_id.toString();
    }
    throw new Error('UsersTable: user row is missing both user_uuid and user_id');
}

function getUserHref(user: SupportedUser): string {
    const userUuid = getUserUuid(user);
    if (userUuid !== null) {
        return `/users/${userUuid}`;
    }
    if ('user_id' in user) {
        return `/users/${user.user_id}`;
    }
    throw new Error('UsersTable: user row is missing both user_uuid and user_id');
}

function getOrganizationSortValue(user: SupportedUser): string | null {
    const roleAssignments = getDisplayRoleAssignments(user);
    const defaultOrganizationName =
        user.default_role_assignment?.organization_name?.trim() ?? '';
    if (defaultOrganizationName.length > 0) {
        return defaultOrganizationName;
    }

    const organizationNames = roleAssignments
        .map((roleAssignment) => roleAssignment.organization_name?.trim() ?? '')
        .filter((organizationName) => organizationName.length > 0)
        .sort((left, right) =>
            left.localeCompare(right, undefined, { sensitivity: 'base', numeric: true })
        );

    return organizationNames[0] ?? null;
}

function getRoleSortValue(user: SupportedUser): string | null {
    const defaultRole = user.default_role_assignment?.role;
    if (defaultRole !== null && defaultRole !== undefined) {
        return defaultRole;
    }

    const roles = getDisplayRoleAssignments(user)
        .map((roleAssignment) => roleAssignment.role)
        .sort((left, right) =>
            left.localeCompare(right, undefined, { sensitivity: 'base', numeric: true })
        );

    return roles[0] ?? null;
}

export function UsersTable({
    users,
}: UsersTableProps) {
    const breadcrumbNavigate = useBreadcrumbNavigation();

    const columns: Column<SupportedUser>[] = useMemo(
        () => [
            {
                key: 'first_name',
                header: 'Name',
                sortable: true,
                sortValue: (user) => user.first_name,
                render: (user) =>
                    user.first_name || <span className="text-muted-foreground">—</span>,
            },
            {
                key: 'last_name',
                header: 'Last Name',
                sortable: true,
                sortValue: (user) => user.last_name,
                render: (user) =>
                    user.last_name || <span className="text-muted-foreground">—</span>,
            },
            {
                key: 'emails',
                header: 'Emails',
                sortable: true,
                sortValue: (user) => user.primary_email,
                render: (user) => (
                    <EmailsCell emails={user.emails} primaryEmail={user.primary_email} />
                ),
            },
            {
                key: 'organization',
                header: 'Organization',
                sortable: true,
                sortValue: getOrganizationSortValue,
                render: (user) => {
                    const roleAssignments = getDisplayRoleAssignments(user);
                    return (
                        <div className="flex flex-col gap-1">
                            {roleAssignments
                                .filter((roleAssignment) => roleAssignment.organization_name !== null)
                                .map((roleAssignment, idx) => {
                                    return (
                                        <OrganizationBubble
                                            key={idx}
                                            organizationName={
                                                roleAssignment.organization_name || null
                                            }
                                            isDefault={roleAssignment.isDefault}
                                        />
                                    );
                                })}
                            {roleAssignments.filter(
                                (roleAssignment) => roleAssignment.organization_name !== null
                            ).length === 0 && (
                                <span className="text-xs text-muted-foreground">
                                    No organization
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                key: 'role',
                header: 'Role',
                sortable: true,
                sortValue: getRoleSortValue,
                render: (user) => (
                    <div className="flex flex-col gap-1">
                        {getDisplayRoleAssignments(user).map((roleAssignment, idx) => (
                            <div key={idx} className="flex items-center gap-1">
                                <RoleBadge role={roleAssignment.role} />
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                key: 'status',
                header: 'Status',
                sortable: true,
                render: (user) => (
                    <Badge
                        variant={
                            user.status === 'ACTIVE'
                                ? 'default'
                                : user.status === 'PENDING'
                                  ? 'secondary'
                                  : 'destructive'
                        }
                    >
                        {user.status}
                    </Badge>
                ),
            },
            {
                key: 'totp_enabled',
                header: 'MFA',
                sortable: true,
                render: (user) => <span>{hasConfiguredMfaMethod(user) ? 'Yes' : 'No'}</span>,
            },
        ],
        []
    );

    return (
        <DataTable
            data={[...users]}
            columns={columns}
            keyExtractor={getUserKey}
            onRowClick={(user) => {
                breadcrumbNavigate({
                    source: 'panel',
                    target: {
                        url: getUserHref(user),
                        title: 'User Details',
                    },
                });
            }}
            paginationMode="none"
        />
    );
}
